import { PersonAddAlt } from '@mui/icons-material';
import { Button } from '@mui/material';
import { t } from 'i18next';
import { Outlet } from 'react-router-dom';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { AuthorizationError } from '../../../common/errors/errors';
import { bind } from '../../../common/utils/decorators';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { PATHS, TabNameLocalizationKeys } from '../../../routing/routes';
import { OrganizationUsersContextType } from '../common/contexts/OrganizationUsersContext';
import { OrganizationUsersRepository } from '../services/OrganizationUsersRepository';

type Props = {};

type State = BaseState & {
  canCreateUsers: boolean;
  nonce: number;
};

export class OrganizationUsersController extends BaseController<Props, State> {
  private readonly usersRepository = new OrganizationUsersRepository();
  private readonly modalManager = new ModalManager();

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.state = {
      ...super.getBaseState(),
      canCreateUsers: false,
      nonce: 0,
    };
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.usersRepository.cancelAllRequests();
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const [canViewUsers, canCreateUsers] = await Promise.all([this.permissionsService.canAccessOrganizationUsers(), this.permissionsService.canCreateUser()]);
    if (!canViewUsers) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }
    this.setState({ canCreateUsers });
  }

  render() {
    const { canCreateUsers, nonce } = this.state;
    return this.renderBase(
      <SecondaryNav
        title={t(TabNameLocalizationKeys.USERS)}
        subtitle={t('usersListSubtitle')}
        rightSection={
          canCreateUsers ? (
            <Button startIcon={<PersonAddAlt />} variant="contained" onClick={this.handleCreateUser}>
              {t('createNewUser')}
            </Button>
          ) : undefined
        }
        getBreadcrumbs={this.getBreadcrumbs}
      >
        <Outlet context={{ nonce } satisfies OrganizationUsersContextType} />
      </SecondaryNav>
    );
  }

  @bind
  private async handleCreateUser() {
    const { userId, assignTestersClicked } = await this.modalManager.createUser();

    if (!userId) return;

    if (assignTestersClicked) {
      this.navigate(PATHS.ORGANIZATION_PROJECTS);
      return;
    }

    // Trigger a refresh of the nested users table
    this.setState((prev) => ({ ...prev, nonce: prev.nonce + 1 }));
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getUsersBreadcrumbs();
  }
}
